const API_BASE = 'https://api.wedmin.biz/makeup-shop';

function getHeaders(): HeadersInit {
  const token = localStorage.getItem('accessToken');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

async function request<T = any>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { ...getHeaders(), ...options?.headers },
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.message || `HTTP ${response.status}`);
  }
  const contentType = response.headers.get('content-type');
  if (contentType?.includes('application/json')) {
    const json = await response.json();
    return json.data ?? json;
  }
  return {} as T;
}

// --- 고객 API ---
export const customerApi = {
  list: (params: Record<string, any>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return request(`/customer?${qs}`);
  },
  create: (data: any) =>
    request('/customer', { method: 'POST', body: JSON.stringify(data) }),
  detail: (id: number, tab = 'OVERVIEW') =>
    request(`/customer/${id}/detail?tab=${tab}`),
  update: (id: number, data: any) =>
    request(`/customer/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: number) =>
    request(`/customer/${id}`, { method: 'DELETE' }),
  memoList: (customerId: number) =>
    request(`/customer/${customerId}/memo`),
  memoCreate: (customerId: number, data: any) =>
    request(`/customer/${customerId}/memo`, { method: 'POST', body: JSON.stringify(data) }),
  memoUpdate: (id: number, data: any) =>
    request(`/customer/memo/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  memoDelete: (id: number) =>
    request(`/customer/memo/${id}`, { method: 'DELETE' }),
};

// --- 계약 API ---
export const contractApi = {
  list: (params: Record<string, any>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return request(`/contract?${qs}`);
  },
  create: (data: any) =>
    request('/contract', { method: 'POST', body: JSON.stringify(data) }),
  detail: (id: number) =>
    request(`/contract/${id}`),
  update: (id: number, data: any) =>
    request(`/contract/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: number) =>
    request(`/contract/${id}`, { method: 'DELETE' }),
  addItem: (contractId: number, data: any) =>
    request(`/contract/${contractId}/item`, { method: 'POST', body: JSON.stringify(data) }),
  updateItem: (itemId: number, data: any) =>
    request(`/contract/item/${itemId}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteItem: (itemId: number) =>
    request(`/contract/item/${itemId}`, { method: 'DELETE' }),
};

// --- 일정 API ---
export const scheduleApi = {
  list: (params: Record<string, any>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return request(`/schedule?${qs}`);
  },
  create: (data: any) =>
    request('/schedule', { method: 'POST', body: JSON.stringify(data) }),
  detail: (id: number) =>
    request(`/schedule/${id}`),
  update: (id: number, data: any) =>
    request(`/schedule/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: number) =>
    request(`/schedule/${id}`, { method: 'DELETE' }),
};

// --- 결제 API ---
export const paymentApi = {
  list: (params: Record<string, any>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return request(`/payment?${qs}`);
  },
  create: (data: any) =>
    request('/payment', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: any) =>
    request(`/payment/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: number) =>
    request(`/payment/${id}`, { method: 'DELETE' }),
  refundList: (params: Record<string, any>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return request(`/payment/refund?${qs}`);
  },
  refundCreate: (data: any) =>
    request('/payment/refund', { method: 'POST', body: JSON.stringify(data) }),
};

// --- 정산 API ---
export const settlementApi = {
  list: (params: Record<string, any>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return request(`/settlement?${qs}`);
  },
  create: (data: any) =>
    request('/settlement', { method: 'POST', body: JSON.stringify(data) }),
  detail: (id: number) =>
    request(`/settlement/${id}`),
  update: (id: number, data: any) =>
    request(`/settlement/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  bulkSettle: (ids: number[], settledDt: string) =>
    request('/settlement/bulk-settle', { method: 'PATCH', body: JSON.stringify({ ids, settledDt }) }),
  bulkCancel: (ids: number[]) =>
    request('/settlement/bulk-cancel', { method: 'PATCH', body: JSON.stringify({ ids }) }),
  partnerSummary: (params: Record<string, any>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return request(`/settlement/partner-summary?${qs}`);
  },
};

// --- 스태프 API ---
export const staffApi = {
  list: (params?: Record<string, any>) => {
    const qs = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
      });
    }
    return request(`/staff?${qs}`);
  },
};

// --- 파트너 업체 API (공통) ---
export const partnerVendorApi = {
  list: (params: Record<string, any>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return fetch(`https://api.wedmin.biz/order/partner-vendors?${qs}`, { headers: getHeaders() })
      .then(res => res.json())
      .then(json => json.data ?? json);
  },
};

// --- 내 업체 정보 API ---
export const myVendorApi = {
  get: () =>
    fetch('https://api.wedmin.biz/vendor/me', { headers: getHeaders() })
      .then(res => res.json())
      .then(json => json.data ?? json),
};

// --- 아티스트 스케줄 그리드 API ---
export const artistGridApi = {
  grid: (date: string) => request(`/schedule/artist-grid?date=${date}`),
};

// --- 재고 관리 API ---
export const inventoryApi = {
  categoryList: () => request('/inventory/category'),
  categoryCreate: (data: any) =>
    request('/inventory/category', { method: 'POST', body: JSON.stringify(data) }),
  categoryUpdate: (id: number, data: any) =>
    request(`/inventory/category/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  categoryDelete: (id: number) =>
    request(`/inventory/category/${id}`, { method: 'DELETE' }),
  list: (params?: Record<string, any>) => {
    const qs = new URLSearchParams();
    if (params) Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return request(`/inventory?${qs}`);
  },
  create: (data: any) =>
    request('/inventory', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: any) =>
    request(`/inventory/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: number) =>
    request(`/inventory/${id}`, { method: 'DELETE' }),
  adjustStock: (id: number, data: any) =>
    request(`/inventory/${id}/stock`, { method: 'POST', body: JSON.stringify(data) }),
  stockLog: (id: number) =>
    request(`/inventory/${id}/log`),
  lowStock: () => request('/inventory/low-stock'),
};

// --- 업체 설정 API (얼리스타트 등) ---
export const vendorConfigApi = {
  get: () => request('/vendor-config'),
  update: (data: any) =>
    request('/vendor-config', { method: 'PATCH', body: JSON.stringify(data) }),
};

// --- 파일 업로드 API ---
export const fileApi = {
  getPresignedUrl: async (params: { mimetype: string; type: string; id: string; extension: string }) => {
    const qs = new URLSearchParams(params);
    const res = await fetch(`https://api.wedmin.biz/v1/file/presigned-url?${qs}`, { headers: getHeaders() });
    if (!res.ok) throw new Error('presigned URL 생성 실패');
    const json = await res.json();
    return json.data ?? json;
  },
  upload: async (file: File, type: string, id: string): Promise<string> => {
    const ext = file.name.split('.').pop() || 'jpg';
    const { url } = await fileApi.getPresignedUrl({ mimetype: file.type, type, id, extension: ext });
    await fetch(url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
    return url.split('?')[0];
  },
};
